import { Money, dec, lastDayOfMonth, localDate, type LocalDate } from "@despejo/domain";
import { type RtsActivity, type RtsBase, type RtsFactorTable, type RtsResult, computeRts } from "./rts.js";

/**
 * RTS quarter from ledger lines (plan §16.1, docs/tax/RTS.md §4): aggregates
 * the quarter's lines into the activity's base, runs computeRts and keeps a
 * per-line trace of what each line adds to the base, to Form 153 (IVA) and to
 * Form 104/105 (utilidades), so every figure can be drilled into and the
 * rows provably add up to it.
 *
 * Base by activity (Decreto 43881-H art. 4; Ley IVA art. 37; Ley Renta art. 73):
 * - purchases in the quarter INCLUDING IVA, by the rate borne (all activities but t, u, v);
 * - + personal/professional services and salaries paid (b, c, q, r, s);
 * - + labour and other production costs (d-p);
 * - fishing (t, u): fuel purchases only; taxi (v): km driven (renta only);
 * - + inventory on hand at entry, first quarter only (art. 9).
 */

export const RTS_QUARTER_VERSION = "rts-quarter@1.0.0";

/**
 * DES-96 added OPENING_INVENTORY (stock on entry, art. 9, from the ledger; it
 * joins `openingInventory`) and EXCLUDED (a ledger line that is not part of
 * any base, shown with its `note`).
 */
export type RtsLineKind = "PURCHASE" | "SERVICES_SALARIES" | "PRODUCTION_COST" | "FUEL" | "OPENING_INVENTORY" | "EXCLUDED";

export interface RtsLedgerLine {
  /** "<transaction id>" / "<transaction id>#<line no>" / "manual:<what>". */
  ref: string;
  date?: string | null;
  label?: string | null;
  kind: RtsLineKind;
  /** IVA rate borne by the purchase ("0.13", "0.01", ...); null or "0" when untaxed. */
  rate: string | null;
  /** Amount in CRC including IVA (business share only). */
  amount: string;
  /** Why an EXCLUDED line is left out (shown in the trace). */
  note?: string | null;
}

export interface RtsQuarterInput {
  /** "YYYY-Qn". */
  period: string;
  activity: RtsActivity;
  lines: RtsLedgerLine[];
  /** Taxi only. */
  km?: string | null;
  /** First quarter in RTS only (art. 9). */
  openingInventory?: string | null;
}

export type RtsLineBucket = "purchase" | "services" | "production" | "fuel" | "inventory" | "km" | "excluded";

export interface RtsLineTrace {
  ref: string;
  bucket: RtsLineBucket;
  /** Normalised rate borne ("0" when untaxed). */
  rate: string;
  amount: string;
  /** What the line adds to Form 153 and Form 104/105 (unrounded; they add up to the totals). */
  iva: string;
  renta: string;
  ivaFactor: string | null;
  rentaFactor: string | null;
  note?: string;
}

export interface RtsQuarterResult {
  aggregator: string;
  engine: string;
  period: string;
  activity: RtsActivity;
  factorVersion: string;
  provisional: boolean;
  base: string;
  /** Form 153. */
  iva: string;
  /** Form 104 (persona física) / 105 (persona jurídica). */
  renta: string;
  baseParts: {
    purchasesByRate: Record<string, string>;
    servicesAndSalaries: string;
    productionCosts: string;
    fuel: string;
    openingInventory: string;
    km: string;
  };
  /** Lines of the quarter that the activity's base does not include (shown, never silently dropped). */
  excludedFromBase: string;
  /** Purchases that count for the annual limit (merchandise and materials incl. IVA; fuel for t/u; none for v). */
  thresholdPurchases: string;
  isNil: boolean;
  exceptions: string[];
  lines: RtsLineTrace[];
}

const SERVICES = new Set<RtsActivity>(["b", "c", "q", "r", "s"]);
const PRODUCERS = new Set<RtsActivity>(["d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p"]);
const FISHING = new Set<RtsActivity>(["t", "u"]);

/** "2026-Q3" -> first/last day and its months ("2026-07".."2026-09"). */
export function quarterBounds(period: string): { year: number; quarter: number; start: LocalDate; end: LocalDate; months: string[] } {
  const m = /^(\d{4})-Q([1-4])$/.exec(period);
  if (!m) throw new RangeError(`not a quarter key: ${period}`);
  const year = Number(m[1]);
  const quarter = Number(m[2]);
  const first = (quarter - 1) * 3 + 1;
  const months = [first, first + 1, first + 2].map((x) => `${year}-${String(x).padStart(2, "0")}`);
  return { year, quarter, start: localDate(year, first, 1), end: lastDayOfMonth(year, first + 2), months };
}

export function normaliseRate(rate: string | null | undefined): string {
  if (rate === null || rate === undefined || rate.trim() === "") return "0";
  const d = dec(rate);
  return d.isZero() ? "0" : d.toString();
}

const r2 = (m: Money) => m.round(2).toFixed(2);

export function computeRtsQuarter(input: RtsQuarterInput, table: RtsFactorTable): RtsQuarterResult {
  const a = input.activity;
  const f = table.factors[a];
  const byRate = new Map<string, Money>();
  let services = Money.zero();
  let production = Money.zero();
  let fuel = Money.zero();
  let excluded = Money.zero();
  let threshold = Money.zero();
  const lines: RtsLineTrace[] = [];

  const flatIva = f && !f.ivaByRate ? (f.iva ?? "0") : null;
  const contribution = (amount: Money, bucket: RtsLineBucket, rate: string): Pick<RtsLineTrace, "iva" | "renta" | "ivaFactor" | "rentaFactor"> => {
    if (!f || bucket === "excluded") return { iva: "0", renta: "0", ivaFactor: null, rentaFactor: null };
    if (bucket === "km") return { iva: "0", renta: amount.times(f.rentaPerKm ?? "0").toString(), ivaFactor: null, rentaFactor: f.rentaPerKm ?? "0" };
    // Per-rate IVA factors apply to purchases only (computeRts: ivaByRate loop over purchasesByRate).
    const ivaFactor = flatIva ?? (bucket === "purchase" ? (f.ivaByRate?.[rate] ?? null) : null);
    return { iva: ivaFactor ? amount.times(ivaFactor).toString() : "0", renta: amount.times(f.renta).toString(), ivaFactor, rentaFactor: f.renta };
  };
  const push = (ref: string, bucket: RtsLineBucket, rate: string, amount: Money, note?: string) => {
    lines.push({ ref, bucket, rate, amount: amount.toString(), ...contribution(amount, bucket, rate), ...(note ? { note } : {}) });
  };

  let ledgerInventory = Money.zero();
  for (const l of input.lines) {
    const amount = Money.of(l.amount);
    const rate = normaliseRate(l.rate);
    if (l.kind === "EXCLUDED") {
      excluded = excluded.add(amount);
      push(l.ref, "excluded", rate, amount, l.note ?? "Fuera de la base del régimen");
      continue;
    }
    if (a === "v") {
      excluded = excluded.add(amount);
      push(l.ref, "excluded", rate, amount, "Taxi: la base es el kilometraje, no las compras");
      continue;
    }
    if (FISHING.has(a)) {
      if (l.kind === "FUEL") {
        fuel = fuel.add(amount);
        threshold = threshold.add(amount);
        push(l.ref, "fuel", rate, amount);
      } else {
        excluded = excluded.add(amount);
        push(l.ref, "excluded", rate, amount, "Pesca: la base es solo el combustible");
      }
      continue;
    }
    if (l.kind === "PURCHASE" || l.kind === "FUEL") {
      byRate.set(rate, (byRate.get(rate) ?? Money.zero()).add(amount));
      threshold = threshold.add(amount);
      push(l.ref, "purchase", rate, amount);
    } else if (l.kind === "SERVICES_SALARIES" && SERVICES.has(a)) {
      services = services.add(amount);
      push(l.ref, "services", rate, amount);
    } else if (l.kind === "PRODUCTION_COST" && PRODUCERS.has(a)) {
      production = production.add(amount);
      push(l.ref, "production", rate, amount);
    } else if (l.kind === "OPENING_INVENTORY") {
      ledgerInventory = ledgerInventory.add(amount);
      push(l.ref, "inventory", "0", amount, l.note ?? "Inventario al ingresar al régimen (art. 9)");
    } else {
      excluded = excluded.add(amount);
      push(l.ref, "excluded", rate, amount, l.kind === "SERVICES_SALARIES" ? `Servicios y salarios no forman la base de la actividad ${a}` : `Costos de producción no forman la base de la actividad ${a}`);
    }
  }
  const manualInventory = Money.of(input.openingInventory ?? "0");
  if (!manualInventory.isZero() && a !== "v" && !FISHING.has(a)) push("manual:inventario-inicial", "inventory", "0", manualInventory, "Inventario al ingresar al régimen (art. 9)");
  // Ledger inventory lines only reach this point for activities with a purchase base (not v, t, u).
  const inventory = manualInventory.add(ledgerInventory);
  const km = Money.of(input.km ?? "0");
  if (a === "v" && !km.isZero()) push("manual:km", "km", "0", km, "Kilómetros recorridos");

  const base: RtsBase = {
    purchasesByRate: Object.fromEntries([...byRate.entries()].sort(([x], [y]) => x.localeCompare(y)).map(([k, v]) => [k, v.toString()])),
    servicesAndSalaries: services.toString(),
    productionCosts: production.toString(),
    fuel: fuel.toString(),
    km: km.toString(),
    ...(a !== "v" && !FISHING.has(a) && !inventory.isZero() ? { openingInventory: inventory.toString() } : {}),
  };
  const r: RtsResult = computeRts(a, base, table);
  const exceptions = [...r.exceptions];

  // The per-line trace must reproduce the engine's totals exactly; anything else is a bug, never a silent difference.
  if (f) {
    const sum = (k: "iva" | "renta") => Money.sum(lines.map((x) => Money.of(x[k]))).round(2).toFixed(2);
    if (sum("iva") !== r.iva || sum("renta") !== r.renta) exceptions.push(`Detalle por línea no cuadra con el cálculo (IVA ${sum("iva")} vs ${r.iva}; renta ${sum("renta")} vs ${r.renta})`);
  }

  return {
    aggregator: RTS_QUARTER_VERSION,
    engine: r.engine,
    period: input.period,
    activity: a,
    factorVersion: r.factorVersion,
    provisional: r.provisional,
    base: r.base,
    iva: r.iva,
    renta: r.renta,
    baseParts: {
      purchasesByRate: Object.fromEntries(Object.entries(base.purchasesByRate).map(([k, v]) => [k, r2(Money.of(v))])),
      servicesAndSalaries: r2(services),
      productionCosts: r2(production),
      fuel: r2(fuel),
      openingInventory: r2(a !== "v" && !FISHING.has(a) ? inventory : Money.zero()),
      km: r2(km),
    },
    excludedFromBase: r2(excluded),
    thresholdPurchases: r2(a === "v" ? Money.zero() : threshold),
    isNil: input.lines.length === 0 && inventory.isZero() && km.isZero(),
    exceptions,
    lines,
  };
}
